import type { MeDevice, Task, WorkerRecord } from '@dyspatch/core';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { useSession } from '@/components/auth/session';
import { GuardedButton, RepoChip } from '@/components/common/bits';
import { AgentMark, ClaudeMark, DeviceMark } from '@/components/common/marks';
import { SortMenu } from '@/components/common/SortMenu';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Badge } from '@/components/ui/badge';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { AskBox, useAsk } from '@/features/actions/AskBox';
import { CommandBox, ConnectDeviceCard } from '@/features/fleet/ConnectDevice';
import { DeviceUpdateNotice } from '@/features/fleet/DeviceVersion';
import { EnrollButton, EnrollComposer } from '@/features/fleet/EnrollComposer';
import { PRESENCE_HUE, PRESENCE_TONE, WorkerTable } from '@/features/fleet/WorkerTable';
import { useRepos, useTasks, useWorkers } from '@/hooks/use-fleet';
import { useHubAction } from '@/hooks/use-hub-action';
import { useSort } from '@/hooks/use-sort';
import { agentSortOptions } from '@/lib/admin-sort';
import { api } from '@/lib/api';
import { heldByWorker, missingCheckouts, presenceOf } from '@/lib/fleet';
import { ago, headroomWindowLabel, minutesSince, percent } from '@/lib/format';
import { devicePresenceOf, PRESENCE_LABEL } from '@/lib/presence';
import { hubKeys } from '@/lib/query';
import { cn } from '@/lib/utils';

/**
 * Agents: the machines the work actually runs on.
 *
 * This used to be `/fleet`, admin-only, and a member's own laptops were a panel
 * on their profile page. That arrangement hid the single most surprising fact
 * about this product from most of the company: the agents are not in a data
 * centre, they are on your colleagues' laptops, under their own Claude
 * subscriptions, and nothing runs while those laptops are shut.
 *
 * So the destination exists for everybody, and the role decides what is on it.
 * A member sees their own devices and what they mean. An admin sees the fleet
 * table they always had. Both reads are already scoped on the server -- a member
 * calling `/api/workers` gets a 403, which is why the two branches call different
 * endpoints rather than one filtered list.
 *
 * The intake adapters that used to share this page moved to the admin overview.
 * They are configuration, not machines, and printing raw environment variable
 * names in the middle of a device table was most of what made this screen read
 * as a terminal someone had wrapped in HTML.
 */

/**
 * One of a member's own laptops, in the words that laptop's owner needs.
 *
 * Two lines, the same height as a fleet row plus one. The first cut was a
 * stat block: three eyebrow-and-figure columns on a grid, a repo list, and
 * then Retire alone at the bottom right after sixty pixels of nothing, which
 * made one laptop cost a quarter of the screen and put the destructive verb
 * as far from the machine's name as the card allowed. Now the name, the state
 * and the verb share a line, and the three facts a person checks (is it
 * running anything, how much window is left, what it is mapped to) share the
 * one under it.
 */
function DeviceCard({
  device,
  held,
  missing,
  onRetire,
  confirming,
}: {
  device: MeDevice;
  /** Unfinished tasks this device holds, as the hub would name them. */
  held: string[] | undefined;
  /** Enabled repos this device has reported no checkout for (issue #101). */
  missing: string[];
  onRetire: () => void;
  confirming: ReactNode;
}) {
  const presence = devicePresenceOf(device);
  const windowLabel = headroomWindowLabel(device.headroomWindow);
  const headroom = Math.max(0, Math.min(1, device.headroom));
  const reported = device.headroomSource !== 'unknown';

  return (
    <li className="px-gutter py-3.5">
      <div className="flex min-w-0 items-center gap-3">
        {/* The machine, drawn as the machine its hostname says it is, and tinted
            by whether anyone is home. See `components/common/marks.tsx`. */}
        <DeviceMark hint={device.hostname || device.name} tone={PRESENCE_HUE[presence]} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 truncate text-md font-medium">{device.name}</span>
            <Badge className={PRESENCE_TONE[presence]}>{PRESENCE_LABEL[presence]}</Badge>
            {/* Only the state that has consequences gets a badge here. A device
                that is merely behind says so in the notice below, in a sentence,
                which is more use than a pill and does not compete with presence. */}
            {device.versionUpdate?.status === 'blocked' ? (
              <Badge variant="destructive" title={device.versionUpdate.detail}>
                Not taking work
              </Badge>
            ) : null}
          </div>
          <div className="text-fg-3 mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs">
            {/* The hostname only where the line has room for it: on a phone it
                wrapped the heartbeat under itself and left the separator
                dangling at the end of the first line. */}
            {device.hostname && device.hostname !== device.name ? (
              <span className="flex min-w-0 items-center gap-x-1.5 max-sm:hidden">
                <span className="truncate">{device.hostname}</span>
                <span aria-hidden>·</span>
              </span>
            ) : null}
            {/* The build is worth showing whether or not it is current: it is the
                first thing anybody asks for when a task behaved differently here
                than on somebody else's laptop. */}
            {device.workerVersion ? (
              <>
                <span className="tnum">worker {device.workerVersion}</span>
                <span aria-hidden>·</span>
              </>
            ) : null}
            <span className="tnum">
              {device.lastHeartbeatAt
                ? `heard from ${ago(minutesSince(device.lastHeartbeatAt))}`
                : 'never checked in'}
            </span>
          </div>
        </div>
        {/* Removing a machine you own used to need an admin: "but how do I remove
            my worker if I want to? Is it something I can do?" was the first
            question anybody asked after connecting one. Killing its token is also
            how you stop a device you no longer trust, which is not a thing to
            queue behind somebody else's availability. It sits on the name's
            line, beside the machine it acts on, rather than on a line of its own. */}
        <GuardedButton
          variant="danger"
          size="xs"
          className="shrink-0"
          refusal={
            held?.length
              ? `${device.name} still holds ${held.join(', ')}. Cancel or finish those first.`
              : null
          }
          title="Take this machine out of the fleet and stop its token working"
          onClick={onRetire}
        >
          Retire
        </GuardedButton>
      </div>

      {/* The facts, on one line under the name and indented past the mark so the
          left edge of the list is still the column of device marks. */}
      <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1.5 pl-11 text-sm">
        <span
          className="tnum flex items-center gap-1.5"
          title="Tasks running now, of the most this device runs at once"
        >
          <ClaudeMark className="text-fg-3 size-3.5" />
          <span>
            <span className="text-foreground font-medium">{device.activeTaskCount}</span>
            <span className="text-fg-3"> of {device.maxConcurrency} running</span>
          </span>
        </span>
        {/* Names the window this percentage binds on (issue #22): "five-hour"
            or "weekly" rather than always implying the five-hour one. The check
            constraint's actual default is 'unknown', not 'unreported'; a device
            that has never reported says so instead of drawing a full bar. */}
        {reported ? (
          <span
            className="flex items-center gap-2"
            title={`${percent(headroom)} of the ${windowLabel || 'Claude'} window left, from ${device.headroomSource}`}
          >
            <span className="bg-raised h-1 w-16 overflow-hidden rounded-full">
              <span
                className={cn(
                  'block h-full rounded-full',
                  headroom > 0.5 ? 'bg-ok' : headroom > 0.2 ? 'bg-warn' : 'bg-bad',
                )}
                style={{ width: `${Math.round(headroom * 100)}%` }}
              />
            </span>
            <span className="tnum">
              <span className="text-foreground font-medium">{percent(headroom)}</span>
              <span className="text-fg-3"> of {windowLabel || 'the'} window left</span>
            </span>
          </span>
        ) : (
          <span className="text-fg-3">window not reported yet</span>
        )}
        <span className="flex min-w-0 flex-wrap items-center gap-1.5">
          {device.repos.length ? (
            device.repos.map((repo) => <RepoChip key={repo} repo={repo} />)
          ) : (
            <span className="text-fg-3">no projects mapped, so nothing is offered to it</span>
          )}
        </span>
      </div>

      {device.versionUpdate ? (
        <DeviceUpdateNotice
          className="mt-2.5 ml-11"
          update={device.versionUpdate}
          name={device.name}
        />
      ) : null}

      {/*
       * A repo an admin registered, that this device's next heartbeat has not
       * named yet (issue #101). The web app cannot clone onto somebody's
       * laptop; the exact command it would otherwise have to go find in a
       * README is what this hands over instead, the same way `ConnectDevice`
       * hands over an enrollment step. It clears itself: the moment a
       * heartbeat reports the repo, `device.repos` includes it and this list
       * comes up empty.
       */}
      {missing.length > 0 ? (
        <div className="border-line-2 mt-2.5 ml-11 space-y-1.5 rounded-md border border-dashed px-2.5 py-2">
          <p className="text-fg-2 text-xs">
            {missing.length === 1
              ? `${device.name} has no checkout of ${missing[0]} yet:`
              : `${device.name} has no checkout of ${missing.length} registered repos yet:`}
          </p>
          {missing.map((repo) => (
            <CommandBox key={repo} command={`dyspatch-worker repos add ${repo}=<path>`} />
          ))}
        </div>
      ) : null}
      {confirming ? <div className="-mx-gutter mt-2.5">{confirming}</div> : null}
    </li>
  );
}

/**
 * The machines that are mine, and the card for adding one.
 *
 * Shared by both halves of this page, because connecting your own laptop is
 * not a member's verb -- it is a person's, and an admin is a person. The admin
 * view had only the fleet and the Enroll device form, which asks for an owner
 * email and a repo list because it exists to provision somebody *else's*
 * machine; an admin who wanted to add their own found themselves filling in
 * their own address on a form for other people.
 *
 * The card shows whether or not there are already devices here: a second
 * machine was exactly as unreachable as the first.
 */
function MyDevices() {
  const devices = useQuery({ queryKey: hubKeys.myDevices, queryFn: api.myDevices });
  const rows = devices.data?.devices ?? [];
  // My own tasks, which is all this needs: the question is only ever whether
  // one of *my* devices is holding something.
  const tasks = useQuery({
    queryKey: hubKeys.myTasks,
    queryFn: async () => (await api.myTasks()).tasks,
  });
  const held = heldByWorker(tasks.data ?? []);
  const repos = useRepos();
  const enabledRepos = (repos.data ?? []).filter((repo) => repo.enabled).map((repo) => repo.slug);

  const ask = useAsk();
  const retire = useHubAction(
    async (device: MeDevice) => {
      await api.retireMyDevice(device.id);
      toast.success(`${device.name} retired. Its token no longer works.`);
    },
    { onDone: () => ask.close() },
  );

  return (
    <>
      {rows.length ? (
        <ul className="divide-line divide-y">
          {rows.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              held={held.get(device.id)}
              missing={missingCheckouts(device, enabledRepos)}
              onRetire={() => {
                retire.reset();
                ask.open('retire-worker', device.id);
              }}
              confirming={
                ask.isOpen('retire-worker', device.id) && ask.ask ? (
                  <AskBox
                    ask={ask.ask}
                    busy={retire.busy}
                    error={retire.error}
                    onCancel={ask.close}
                    onConfirm={() => retire.run(device)}
                  />
                ) : null
              }
            />
          ))}
        </ul>
      ) : null}
      <ConnectDeviceCard />
    </>
  );
}

function MemberView() {
  const devices = useQuery({ queryKey: hubKeys.myDevices, queryFn: api.myDevices });
  const rows = devices.data?.devices ?? [];

  return (
    <Page className="max-w-[1000px]">
      <div className="dy-stack">
        <Section>
          <SectionHead
            title="Your agents"
            meta={rows.length ? `${rows.length} enrolled` : undefined}
          />
          <Surface>
            <MyDevices />
          </Surface>
        </Section>

        <Section>
          <SectionHead title="What an agent is" />
          <Surface>
            <div className="text-fg-2 space-y-3 px-gutter py-4 text-base">
              <p>
                An agent is Claude Code running on a real laptop, under that person's own
                subscription. Dyspatch never calls a model itself and holds no model credential; it
                offers work to whichever machine is free and records what came back.
              </p>
              <p>
                That has one consequence worth knowing:{' '}
                <strong>work only moves while somebody's laptop is awake.</strong> A queue that is
                not draining overnight usually means every machine mapped to that repo is shut, not
                that anything is broken.
              </p>
            </div>
          </Surface>
        </Section>
      </div>
    </Page>
  );
}

/**
 * How wide the admin half of this page is allowed to be.
 *
 * It was the console's full 1560px, and the fleet table has six columns of
 * short facts: a hostname, a one-word presence, a fraction, a couple of repo
 * chips and a duration. At 1560px every one of them floats in its own field of
 * whitespace and the two verbs at the end of a row sit six hundred pixels away
 * from the machine they act on, which is most of what made this screen read as
 * a spreadsheet with the data deleted. At this measure the row is dense enough
 * that a device and the buttons for it are one object.
 */
const FLEET_WIDTH = '1180px';
/**
 * The same number as a literal class, because Tailwind reads source text: a
 * `max-w-[${FLEET_WIDTH}]` template produces no rule at all and the page
 * silently keeps the 1560px default. Kept next to its pair so the two cannot
 * drift without both being in view.
 */
const FLEET_WIDTH_CLASS = 'max-w-[1180px]';

/**
 * "3 devices · 2 online · 1 working · 1 of 5 slots busy", for the header's
 * clause. The header used to say "3 enrolled · 0 working" and the fleet
 * heading under it "3 devices · 1 online · 0 working · 0 of 5 slots busy",
 * which is the same page counting itself twice in two vocabularies. How many
 * are awake, how many are working and how much capacity is in use is what an
 * admin opens this page to find out, so it is the one line under the title.
 */
function fleetSummary(rows: WorkerRecord[]): string {
  const online = rows.filter((worker) => presenceOf(worker) !== 'offline').length;
  const working = rows.filter((worker) => presenceOf(worker) === 'busy').length;
  const slots = rows.reduce((total, worker) => total + worker.maxConcurrency, 0);
  const busy = rows.reduce((total, worker) => total + worker.activeTaskCount, 0);
  return [
    `${rows.length} device${rows.length === 1 ? '' : 's'}`,
    `${online} online`,
    `${working} working`,
    `${busy} of ${slots} slot${slots === 1 ? '' : 's'} busy`,
  ].join(' · ');
}

function AdminView({
  enrolling,
  onClose,
  rows,
  pending,
  tasks,
  sorting,
}: {
  enrolling: boolean;
  onClose: () => void;
  /** Already in the reader's chosen order; see `agentSortOptions` on `AgentsPage`. */
  rows: WorkerRecord[];
  pending: boolean;
  tasks: Task[];
  sorting: ReturnType<typeof useSort<WorkerRecord>>;
}) {
  return (
    <Page className={FLEET_WIDTH_CLASS}>
      <div className="dy-stack">
        <EnrollComposer open={enrolling} onClose={onClose} />

        {/* An admin has a laptop too, and self-serve was written only into the
            member's half of this page: pressing Enroll device as an admin
            opened the form for provisioning somebody *else's* machine, asking
            for an owner email and repos, when what was meant was "connect
            mine". Their own machines come first, on the same card a member
            gets; the fleet is below, where an admin's other job is. */}
        <Section>
          <SectionHead title="Your agents" />
          <Surface>
            <MyDevices />
          </Surface>
        </Section>

        <Section>
          {/* No meta on this heading: the whole fleet in one line is the page
              header's clause, two regions up, and the same four numbers printed
              twice within one screen read as two different facts. */}
          <SectionHead
            title="Everyone's devices"
            actions={
              <SortMenu options={sorting.options} state={sorting.state} onChange={sorting.choose} />
            }
          />
          {pending && !rows.length ? (
            <Surface>
              <p className="text-fg-3 px-gutter py-10 text-center text-sm">Loading the fleet…</p>
            </Surface>
          ) : rows.length ? (
            <WorkerTable workers={rows} tasks={tasks} />
          ) : (
            <Surface>
              <div className="px-gutter py-12 text-center">
                <AgentMark className="mx-auto mb-3 size-10" />
                <p className="text-foreground text-lg font-medium">No devices enrolled</p>
                <p className="text-fg-3 mx-auto mt-1.5 max-w-md text-sm">
                  Nothing can run yet. Enrolling mints a one-time code somebody runs on their own
                  laptop; the agent then works under their Claude subscription, not the hub's.
                </p>
              </div>
            </Surface>
          )}
        </Section>
      </div>
    </Page>
  );
}

export function AgentsPage() {
  const { session } = useSession();
  const workers = useWorkers(session.isAdmin);
  const tasks = useTasks(session.isAdmin);
  // The composer's open state lives here rather than in `AdminView`, because the
  // button that opens it is in the header and the form it opens is in the body.
  const [enrolling, setEnrolling] = useState(false);
  const rows = workers.data ?? [];
  const taskRows = tasks.data ?? [];

  // One pass over the task list rather than a lookup per row; see the same
  // choice on `/jobs` (`BriefsPage`).
  const tasksRunByWorker = new Map<string, number>();
  for (const task of taskRows) {
    if (!task.assignedWorkerId) continue;
    tasksRunByWorker.set(
      task.assignedWorkerId,
      (tasksRunByWorker.get(task.assignedWorkerId) ?? 0) + 1,
    );
  }

  const sorting = useSort(
    agentSortOptions((worker) => tasksRunByWorker.get(worker.id) ?? 0),
    (worker) => worker.id,
  );
  const sortedRows = sorting.sort(rows);

  return (
    <>
      <ViewHeader
        title="Agents"
        icon={ClaudeMark}
        tone="var(--dy-info)"
        sub={session.isAdmin ? fleetSummary(rows) : 'the machines your work runs on'}
        width={session.isAdmin ? FLEET_WIDTH : '1000px'}
        actions={session.isAdmin ? <EnrollButton onClick={() => setEnrolling(true)} /> : undefined}
      />
      {session.isAdmin ? (
        <AdminView
          enrolling={enrolling}
          onClose={() => setEnrolling(false)}
          rows={sortedRows}
          pending={workers.isPending}
          tasks={taskRows}
          sorting={sorting}
        />
      ) : (
        <MemberView />
      )}
    </>
  );
}
